import { Component, inject } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, collection, collectionData, addDoc, doc, deleteDoc, updateDoc, query, orderBy } from '@angular/fire/firestore';
import { Observable, firstValueFrom } from 'rxjs';

export interface Account {
  id?: string;
  code: string;
  name: string;
  category: string;
}

@Component({
  selector: 'app-accounts',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule],
  templateUrl: './accounts.component.html',
  styleUrls: ['../home/home.component.css'] // 👈 REUSE CSS from Home
})
export class AccountsComponent {
  private firestore = inject(Firestore);
  
  accounts$: Observable<Account[]>;

  constructor() {
    const col = collection(this.firestore, 'chart_of_account');
    const q = query(col, orderBy('code'));
    this.accounts$ = collectionData(q, { idField: 'id' }) as Observable<Account[]>;
  }

  showModal = false;
  isSubmitted = false;
  editingId: string | null = null;
  deletingId: string | null = null;
  duplicateError = false;

  newAccount: Account = {
    code: '', name: '', category: 'Asset'
  };

  openModal() {
    this.showModal = true;
    this.isSubmitted = false;
    this.editingId = null;
    this.duplicateError = false;
    this.newAccount = { code: '', name: '', category: 'Asset' };
  }

  closeModal() { this.showModal = false; this.isSubmitted = false; this.editingId = null; this.duplicateError = false; }

  editAccount(acc: any) {
    this.editingId = acc.id;
    this.newAccount = { ...acc };
    this.showModal = true;
  }

  async deleteAccount(id: string | undefined) {
    if (!id) return;
    if (confirm('Are you sure you want to delete this account?')) {
      this.deletingId = id;
      try {
        await deleteDoc(doc(this.firestore, 'chart_of_account', id));
      } finally {
        this.deletingId = null;
      }
    }
  }

  async saveAccount() {
    this.isSubmitted = true;
    this.duplicateError = false;
    if (!this.newAccount.code || !this.newAccount.name || !this.newAccount.category) return;

    const accounts = await firstValueFrom(this.accounts$);
    const isDuplicate = accounts.some(acc => acc.code === this.newAccount.code && acc.id !== this.editingId);

    if (isDuplicate) {
      this.duplicateError = true;
      return;
    }

    // Remove ID from data before saving to Firestore
    const { id, ...dataToSave } = this.newAccount;

    if (this.editingId) {
      const docRef = doc(this.firestore, 'chart_of_account', this.editingId);
      await updateDoc(docRef, dataToSave);
      this.closeModal();
    } else {
      const col = collection(this.firestore, 'chart_of_account');
      await addDoc(col, dataToSave);
      this.closeModal();
    }
  }
}